import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Clock, Briefcase, Users, Heart, Zap, Award, CheckCircle } from 'lucide-react';
import { careers } from '../mock';

export const Careers = () => {
  return (
    <div className="min-h-screen bg-[#0a2540]">
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a2540] via-[#1e4062] to-[#0a2540]"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0, 168, 255, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(76, 211, 255, 0.15) 0%, transparent 50%)',
          animation: 'pulse 4s ease-in-out infinite'
        }}></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-slide-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold led-text mb-6 animate-led-pulse" style={{ textShadow: '0 0 20px #00a8ff, 0 0 40px #00a8ff, 0 0 60px #4cd3ff' }}>
              JOIN OUR TEAM
            </h1>
            <p className="text-base md:text-lg text-[#00a8ff] font-medium" style={{ textShadow: '0 0 10px #00a8ff' }}>
              Build your career with talented professionals who are passionate about technology
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl font-bold led-text mb-4 animate-led-pulse" style={{ textShadow: '0 0 15px #00a8ff, 0 0 30px #4cd3ff' }}>
              WHY WORK WITH US?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl backdrop-blur-sm hover:scale-105 transition-all duration-300 animate-slide-up" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.3)' }}>
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[rgba(0,168,255,0.3)] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.6)' }}>
                  <Zap className="text-[#00a8ff]" size={40} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" style={{ textShadow: '0 0 10px #00a8ff' }}>Innovation First</h3>
                <p className="text-[#00a8ff]" style={{ textShadow: '0 0 5px #00a8ff' }}>
                  Work on cutting-edge projects using the latest technologies and methodologies.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl backdrop-blur-sm hover:scale-105 transition-all duration-300 animate-slide-up" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.3)', animationDelay: '0.1s' }}>
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[rgba(0,168,255,0.3)] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.6)' }}>
                  <Users className="text-[#00a8ff]" size={40} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" style={{ textShadow: '0 0 10px #00a8ff' }}>Great Team Culture</h3>
                <p className="text-[#00a8ff]" style={{ textShadow: '0 0 5px #00a8ff' }}>
                  Collaborate with talented individuals in a supportive and inclusive environment.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl backdrop-blur-sm hover:scale-105 transition-all duration-300 animate-slide-up" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.3)', animationDelay: '0.2s' }}>
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[rgba(0,168,255,0.3)] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.6)' }}>
                  <Award className="text-[#00a8ff]" size={40} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" style={{ textShadow: '0 0 10px #00a8ff' }}>Growth Opportunities</h3>
                <p className="text-[#00a8ff]" style={{ textShadow: '0 0 5px #00a8ff' }}>
                  Continuous learning, professional development, and career advancement.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Perks Section */}
      <section className="py-16 relative">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 led-text animate-led-pulse" style={{ textShadow: '0 0 15px #00a8ff, 0 0 30px #4cd3ff' }}>
              PERKS & BENEFITS
            </h2>
            <Card className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl backdrop-blur-sm animate-slide-up" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.3)' }}>
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    'Competitive salary packages',
                    'Health insurance coverage',
                    'Flexible work arrangements',
                    'Remote work options',
                    'Professional development budget',
                    'Latest tech equipment',
                    'Team building activities',
                    'Work-life balance'
                  ].map((perk, idx) => (
                    <div key={idx} className="flex items-center space-x-3 group">
                      <div className="w-8 h-8 bg-[rgba(0,168,255,0.3)] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform" style={{ boxShadow: '0 0 10px rgba(0,168,255,0.5)' }}>
                        <CheckCircle size={18} className="text-[#00a8ff]" />
                      </div>
                      <span className="text-white font-medium group-hover:text-[#4cd3ff] transition-colors" style={{ textShadow: '0 0 5px #00a8ff' }}>{perk}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl font-bold led-text mb-4 animate-led-pulse" style={{ textShadow: '0 0 15px #00a8ff, 0 0 30px #4cd3ff' }}>
              OPEN POSITIONS
            </h2>
            <p className="text-base text-[#00a8ff] font-medium" style={{ textShadow: '0 0 8px #00a8ff' }}>Find your next opportunity</p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {careers.map((job, index) => (
              <Card key={job.id} className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl backdrop-blur-sm hover:scale-102 transition-all duration-300 animate-slide-up" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.3)', animationDelay: `${index * 0.1}s` }}>
                <CardContent className="p-8">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-white mb-4 led-text" style={{ textShadow: '0 0 10px #00a8ff' }}>{job.title}</h3>
                      
                      <div className="flex flex-wrap gap-3 mb-6">
                        <div className="flex items-center space-x-2 bg-[rgba(0,168,255,0.2)] border border-[#00a8ff] rounded-lg px-3 py-1.5">
                          <Briefcase size={16} className="text-[#00a8ff]" />
                          <span className="text-white text-sm font-medium">{job.department}</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-[rgba(0,168,255,0.2)] border border-[#00a8ff] rounded-lg px-3 py-1.5">
                          <MapPin size={16} className="text-[#00a8ff]" />
                          <span className="text-white text-sm font-medium">{job.location}</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-[rgba(0,168,255,0.2)] border border-[#00a8ff] rounded-lg px-3 py-1.5">
                          <Clock size={16} className="text-[#00a8ff]" />
                          <span className="text-white text-sm font-medium">{job.type}</span>
                        </div>
                      </div>

                      <p className="text-[#00a8ff] mb-6" style={{ textShadow: '0 0 5px #00a8ff' }}>{job.description}</p>

                      <div>
                        <h4 className="font-bold text-white mb-3" style={{ textShadow: '0 0 8px #00a8ff' }}>Requirements:</h4>
                        <ul className="space-y-2">
                          {job.requirements.map((req, idx) => (
                            <li key={idx} className="text-sm text-[#00a8ff] flex items-start">
                              <span className="mr-3 text-[#4cd3ff] font-bold">▸</span>
                              <span style={{ textShadow: '0 0 5px #00a8ff' }}>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="lg:ml-6 flex lg:flex-col gap-3">
                      <Button className="w-full lg:w-auto bg-[#00a8ff] text-[#0a2540] hover:bg-[#4cd3ff] font-bold px-8 py-6 transition-all duration-300 hover:scale-105" style={{ boxShadow: '0 0 20px #00a8ff' }}>
                        Apply Now
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};